import { notifyApi } from '@/api/su/notify';
import type { INotificationState } from '@/api/types';
import type { RootState } from '@/store';
import { KnownErrors } from '@/store/enums';
import type { INotificationsRaw, IThunkAPI } from '@/store/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const initialState: INotificationState = {
  notificationsList: null,
  isNotificationsError: false,
  isNotificationsSuccess: false,
  isNotificationsFetching: false,
  selectedNotificationId: 0,
};

export const getNotifyJobListByClientId = createAsyncThunk<INotificationsRaw, number, IThunkAPI>(
  'get/notififation/su/list',
  async (id, thunkAPI) => {
    try {
      const response = await notifyApi.getNotifyJobListByClientId(id);
      let data = response.data;
      if (response.data === KnownErrors.ItemNotFound) {
        data = [];
      }
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

const getNotificationStatus = (statusId: number) => {
  if (statusId === 330) {
    return 'Pending';
  } else if (statusId === 331) {
    return 'Processing';
  } else if (statusId === 332) {
    return 'Complete';
  } else {
    return '';
  }
};

export const notificationListSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    clearNotifications(state) {
      state.notificationsList = [];
    },
    updateSelectedNotification(state, { payload }) {
      state.selectedNotificationId = payload?.selectedNotificationId;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getNotifyJobListByClientId.fulfilled, (state, action) => {
        if (action.payload && Array.isArray(action.payload) && action.payload?.length > 0) {
          state.notificationsList = action.payload.map(notify => {
            return {
              ...notify,
              statusText: getNotificationStatus(notify.status),
            };
          });
        } else {
          state.notificationsList = [];
        }
        state.isNotificationsSuccess = true;
        state.isNotificationsError = false;
        state.isNotificationsFetching = false;
      })
      .addCase(getNotifyJobListByClientId.rejected, state => {
        state.isNotificationsError = true;
        state.isNotificationsSuccess = false;
        state.isNotificationsFetching = false;
      })
      .addCase(getNotifyJobListByClientId.pending, state => {
        state.isNotificationsFetching = true;
      });
  },
});

// actions
export const { clearNotifications, updateSelectedNotification } = notificationListSlice.actions;

// selectors
export const notificationList = (state: RootState): INotificationState['notificationsList'] =>
  state.notifications.notificationsList;
export const isNotficationsFetching = (state: RootState): boolean => state.notifications.isNotificationsFetching;

export default notificationListSlice.reducer;
